import { Injectable, Injector } from "@angular/core";
import { FeatureName } from "@common/ADAPT.Common.Model/embed/feature-name.enum";
import { Organisation } from "@common/ADAPT.Common.Model/organisation/organisation";
import { IChatAiOrgProvider, IMegaPromptLabel } from "@common/lib/chat-ai/chat-ai-org-provider.interface";
import { ChatAiUiService } from "@common/lib/chat-ai/chat-ai-ui.service";
import { IMegaPromptEntry, MegaPromptMetadata } from "@common/lib/chat-ai/mega-prompts";
import { RouteService } from "@common/route/route.service";
import { RouteEventsService } from "@common/route/route-events.service";
import { debounceTime, EMPTY, map, Observable, of, shareReplay, startWith, switchMap } from "rxjs";

@Injectable({
    providedIn: "root",
})
export class NimbusChatAiOrgService implements IChatAiOrgProvider {
    public readonly applicableMegaPromptLabels$: Observable<IMegaPromptLabel[]>;
    public readonly authorisationChanged$: Observable<void> = EMPTY;
    public readonly organisationSwitching$: Observable<Organisation | undefined> = EMPTY;
    public readonly organisationChanged$: Observable<Organisation | undefined> = EMPTY;

    public constructor(
        routeEventsService: RouteEventsService,
        routeService: RouteService,
        injector: Injector,
    ) {
        this.applicableMegaPromptLabels$ = routeEventsService.navigationEnd$.pipe(
            startWith(undefined),
            debounceTime(500),
            switchMap(() => {
                const route = routeService.currentRoute;
                if (!route?.data?.megaPrompts?.length) {
                    return of([] as IMegaPromptLabel[]);
                }

                const chatUiService = injector.get(ChatAiUiService);
                return chatUiService.getMegaPromptNames$(true).pipe(
                    map((megaPromptNames) => megaPromptNames
                        .filter((name) => {
                            const matchedEntry: IMegaPromptEntry | undefined = route.data!.megaPrompts.find(
                                (entry: IMegaPromptEntry) => name === entry.name);
                            if (matchedEntry?.condition && !matchedEntry.condition()) {
                                return false;
                            }

                            // this is nimbus - only stakeholder manager can login - so no need to check for permissions
                            return !!matchedEntry;
                        })
                        .map((promptName) => ({ promptName, label: MegaPromptMetadata[promptName].label })),
                    ));
            }),
            shareReplay(1),
        );
    }

    public promiseToCheckIfFeatureActive(_name: FeatureName): Promise<boolean> {
        // nimbus won't have org feature status to check against - so simply return true here
        return Promise.resolve(true);
    }

    public getOrganisationId(): number {
        return 0;
    }

    public notifyOrganisationChangedEvent(): void {
        // no-op: nimbus has no organisation context
    }
}
